import React, { Fragment, forwardRef } from 'react'
import PropTypes, { node } from 'prop-types'
import cx from 'classnames'
import InputMask from 'sb-text-mask'
import { useUniqueId } from 'sb-hooks'
import { intlMessageShape, injectIntl } from 'sb-react-intl'

import Textarea from 'components/_new/ui/Textarea/Textarea'
import Icon from 'components/_new/ui/Icon/Icon'
import Message from 'components/text/Message/Message'
import CircleLoader from 'components/loaders/CircleLoader/CircleLoader'
import VisuallyHidden from 'components/ui/VisuallyHidden/VisuallyHidden'
import FieldError from 'components/_new/forms/FieldError/FieldError'

import s from './StandardInputView.scss'

import eraseImage from './images/erase.svg'


const StandardInputView = (props) => {
  const {
    className, type, placeholder, label, value, error, mask, maskChar, maskGuide,
    readOnly, errored, loading, withBorder, withEraser, beforeNode, afterNode,
    icon, iconColor, iconButtonProps,
    onChange, onBlur, onFocus, onErase, onSubmit, onTouchStart, onBeforeNodeClick, onAfterNodeClick, onIconClick,
    dataTestId, id, rows, autosize, intl, forwardedRef: ref,
    autoComplete, eraserLabel, semiboldPlaceholder,
  } = props

  const isTextArea  = Boolean(rows)
  const uniqueId    = useUniqueId()
  const inputId     = id || `input_${uniqueId}` // for "htmlFor" attribute

  const contentClassName = cx(s.content, className, {
    [s.errored]: errored || error,
    [s.bordered]: withBorder,
    [s.disabled]: readOnly,
  })

  const inputClassName = cx(s.input, {
    [s.withoutBeforeNode]: !beforeNode,
    [s.textArea]: isTextArea,
    [s.semiboldPlaceholder]: semiboldPlaceholder,
  })

  const nodeProps = {
    id: inputId,
    className: inputClassName,
    type,
    value,
    placeholder: intl.message(placeholder),
    readOnly,
    disabled: readOnly,
    tabIndex: readOnly ? -1 : 0,
    autoComplete,
    onChange,
    onBlur,
    onFocus,
    onTouchStart,
    'data-testid': dataTestId,
  }

  let component = 'input'

  if (mask) {
    nodeProps.forwardedRef = ref
    nodeProps.mask = mask
    nodeProps.guide = maskGuide
    nodeProps.placeholderChar = maskChar

    component = InputMask
  }
  if (autosize) {
    nodeProps.ref = ref
    nodeProps.rows = rows
    delete nodeProps.type

    component = Textarea
  }
  else if (isTextArea) {
    nodeProps.rows = rows
    nodeProps.ref  = ref

    component = 'textarea'
  }
  else {
    nodeProps.ref = ref
  }

  return (
    <Fragment>
      <div className={contentClassName}>
        {
          Boolean(label || placeholder) && (
            <VisuallyHidden>
              <Message text={label || placeholder} tag="label" htmlFor={inputId} />
            </VisuallyHidden>
          )
        }
        {
          Boolean(beforeNode) && (
            <div className={s.beforeContainer} onClick={onBeforeNodeClick}>
              {beforeNode}
            </div>
          )
        }
        {
          React.createElement(component, nodeProps)
        }
        {
          Boolean(!loading && typeof onSubmit === 'function') && (
            <button className={s.submitButton} type="button" onClick={onSubmit}>
              Apply
            </button>
          )
        }
        {
          Boolean(!loading && withEraser && value) && (
            <button className={s.eraser} type="button" onClick={onErase} aria-label={eraserLabel}>
              <img src={eraseImage} alt="" />
            </button>
          )
        }
        {
          Boolean(!loading && icon) && (
            <button
              type="button"
              {...iconButtonProps}
              className={cx(s.iconButton, iconButtonProps.className)}
              onClick={onIconClick}
            >
              <Icon className={s.icon} name={icon} color={iconColor} />
            </button>
          )
        }
        {
          Boolean(loading) && (
            <div className={s.spinner}>
              <CircleLoader />
            </div>
          )
        }
        {
          Boolean(afterNode) && (
            <div className={s.afterContainer} onClick={onAfterNodeClick}>
              {afterNode}
            </div>
          )
        }
      </div>
      {
        Boolean(error) && (
          <FieldError message={error} />
        )
      }
    </Fragment>
  )
}

StandardInputView.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  value: PropTypes.string,
  rows: PropTypes.number,
  autosize: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(intlMessageShape),
  ]),
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  iconButtonProps: PropTypes.object,
  maskChar: PropTypes.string,
  maskGuide: PropTypes.bool,
  readOnly: PropTypes.bool,
  loading: PropTypes.bool,
  withBorder: PropTypes.bool,
  withEraser: PropTypes.bool,
  semiboldPlaceholder: PropTypes.bool,
  beforeNode: PropTypes.node,
  afterNode: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onErase: PropTypes.func,
  onSubmit: PropTypes.func,
  onIconClick: PropTypes.func,
  onBeforeNodeClick: PropTypes.func,
  onAfterNodeClick: PropTypes.func,
  dataTestId: PropTypes.string,
}

StandardInputView.defaultProps = {
  maskGuide: false,
  withBorder: true,
  iconButtonProps: {},
}

const StandardInputViewWithIntl = injectIntl(StandardInputView)


export default forwardRef((props, ref) => (
  <StandardInputViewWithIntl forwardedRef={ref} {...props} />
))
